import { createContext, useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import axios from "../api/axios";
import {
  MePermissions,
  Permission,
  StoredAdminUser,
  optimisticPermissions,
  readStoredUser,
} from "./permissions";

/**
 * Single source of truth for "who is signed in and what may they do".
 *
 * The answer comes from the SERVER (GET /me/permissions), which resolves it
 * from the roles assigned in the database. Nothing about the permission set is
 * decided in this bundle, so an administrator can create a role, tick a few
 * boxes, and the operator sees the effect on their next page load - no
 * frontend release involved.
 *
 * While the request is in flight we fall back to `optimisticPermissions`, which
 * grants everything to a legacy super-admin and nothing to anyone else. That
 * avoids a flash of "Acces refuse" for the people who have full access, and
 * fails closed for everyone else.
 */
export interface AdminAuth {
  user: StoredAdminUser | null;
  permissions: string[];
  roles: MePermissions["roles"];
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
  /** True when a token exists but the account holds no permission at all. */
  isAuthenticatedWithoutAccess: boolean;
  /** The server has answered; before that, `permissions` is optimistic. */
  isResolved: boolean;
  can: (permission: Permission | string) => boolean;
  canAny: (permissions: (Permission | string)[]) => boolean;
  canAll: (permissions: (Permission | string)[]) => boolean;
  refresh: () => void;
}

const readToken = (): string | null => {
  try {
    return localStorage.getItem("token");
  } catch {
    return null;
  }
};

export const permissionKeys = {
  me: () => ["me", "permissions"] as const,
};

const AuthContext = createContext<AdminAuth | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useProvideAuth();
  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

const useProvideAuth = (): AdminAuth => {
  const queryClient = useQueryClient();

  const [session, setSession] = useState(() => ({
    user: readStoredUser(),
    token: readToken(),
  }));

  useEffect(() => {
    // `storage` fires for OTHER tabs, which is the cross-tab logout case:
    // signing out in one tab must not leave another rendering the pricing
    // editor with a stale permission set.
    const sync = () => setSession({ user: readStoredUser(), token: readToken() });
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const hasToken = !!session.token;

  const { data, isSuccess } = useQuery({
    queryKey: permissionKeys.me(),
    queryFn: async (): Promise<MePermissions> => {
      const response = await axios.get("/me/permissions");
      return response.data?.data ?? response.data;
    },
    enabled: hasToken,
    // Permissions change rarely but must not be stale for long after an
    // administrator revokes a role.
    staleTime: 60_000,
    retry: 1,
  });

  return useMemo(() => {
    const resolved = isSuccess && !!data;
    const permissions = resolved
      ? data!.permissions ?? []
      : optimisticPermissions(session.user);

    const can = (permission: Permission | string) =>
      permissions.includes(permission);

    return {
      user: session.user,
      permissions,
      roles: data?.roles ?? [],
      isSuperAdmin:
        data?.is_super_admin ?? Number(session.user?.is_super_admin) === 1,
      isAuthenticated: hasToken && permissions.length > 0,
      isAuthenticatedWithoutAccess: hasToken && resolved && permissions.length === 0,
      isResolved: resolved,
      can,
      canAny: (list) => list.some(can),
      canAll: (list) => list.every(can),
      refresh: () =>
        queryClient.invalidateQueries({ queryKey: permissionKeys.me() }),
    };
  }, [data, isSuccess, session, hasToken, queryClient]);
};

/**
 * Components must not read localStorage or refetch permissions themselves -
 * going through the provider means one request per session rather than one per
 * component, and one place to change when the session shape moves.
 */
export const useAuth = (): AdminAuth => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
};

export const useHasPermission = (permission: Permission | string): boolean =>
  useAuth().can(permission);

export default useAuth;
